// Mock implementation of dealsAPI aligned with the backend response shape.
// localStorage is the source of truth; invoices / shipments live in their own
// side tables keyed by dealId and are kept in sync with the parent deal's
// `invoiceSummary` / `shipmentProgress` aggregates after every mutation.

import type {
  CancelDealReqBody,
  CreateDealReqBody,
  CreateManualInvoiceReqBody,
  CreateShipmentReqBody,
  DealEntity,
  InvoiceEntity,
  ListDealsQuery,
  ListPaginatedDealsResult,
  ProductGroupEntity,
  SendInvoiceReqBody,
  ShipmentEntity,
  UpdateDealReqBody,
  UpdateInvoiceReqBody,
  UpdateShipmentReqBody,
  VoidInvoiceReqBody,
} from '../../../apis/dealsAPI'

import {
  readDealInvoices,
  readDealShipments,
  readDeals,
  readState,
  writeDealInvoices,
  writeDealShipments,
  writeDeals,
  writeState,
} from './hydrate'
import {
  invoicePaidCents,
  invoiceTotalCents,
  nextInvoiceNumber,
  recalculateInvoiceState,
} from './invoiceCalc'
import { computeLineTotalCents } from '../utils/invoiceMath'
import { formatPeriodLabel } from '../utils/formatCents'
import { delay } from './storage'
import { PRODUCT_OPTIONS } from './seed/productOptions'
import {
  dealMatchesPayment,
  dealMatchesSearch,
  dealMatchesStatus,
  type PaymentFilter,
  type StatusFilter,
} from '../utils/dealUrgency'

const DEFAULT_PAGE_SIZE = 50

const STATUS_FILTERS: readonly StatusFilter[] = [
  'all',
  'healthy',
  'soon',
  'expired',
  'drafts',
  'canceled',
  'archived',
]
const PAYMENT_FILTERS: readonly PaymentFilter[] = [
  'all',
  'paid',
  'partial',
  'unpaid',
  'scheduled',
]

function isStatusFilter(value: string | undefined): value is StatusFilter {
  return value !== undefined && (STATUS_FILTERS as readonly string[]).includes(value)
}

function isPaymentFilter(value: string | undefined): value is PaymentFilter {
  return value !== undefined && (PAYMENT_FILTERS as readonly string[]).includes(value)
}

const cloneDeal = (deal: DealEntity): DealEntity => JSON.parse(JSON.stringify(deal)) as DealEntity

// Projects the per-deal archived/canceled lifecycle stored in the state bucket
// back onto the deal entity. Seed deals stay pristine; the parallel state
// bucket is what `archiveDeal` / `cancelDeal` mutate, hydrated on every read.
function applyArchivedFlags(deals: DealEntity[]): DealEntity[] {
  const state = readState()
  const archivedSet = new Set(state.archivedDealIds)
  const canceledMap = state.canceledDeals
  return deals.map((deal) => {
    const archivedAt = archivedSet.has(deal.id)
      ? deal.archivedAt ?? new Date(0).toISOString()
      : null
    const canceled = canceledMap[String(deal.id)]
    const lifecycle = canceled
      ? {
          ...deal.lifecycle,
          status: 'canceled' as const,
          canceledMode: canceled.mode,
          canceledAt: canceled.canceledAt,
        }
      : deal.lifecycle
    return {
      ...deal,
      archivedAt,
      lifecycle,
    }
  })
}

const mustFindDeal = (dealId: string): DealEntity => {
  const deals = readDeals()
  const deal = deals.find((entry) => entry.id === dealId)
  if (!deal) throw new Error(`mock dealsApi: deal id=${dealId} not found`)
  return deal
}

function getInvoicesForDeal(dealId: string): InvoiceEntity[] {
  const map = readDealInvoices()
  return map[dealId] ?? []
}

function getShipmentsForDeal(dealId: string): ShipmentEntity[] {
  const map = readDealShipments()
  return map[dealId] ?? []
}

function setInvoicesForDeal(dealId: string, invoices: InvoiceEntity[]): void {
  const map = readDealInvoices()
  map[dealId] = invoices
  writeDealInvoices(map)
}

function setShipmentsForDeal(dealId: string, shipments: ShipmentEntity[]): void {
  const map = readDealShipments()
  map[dealId] = shipments
  writeDealShipments(map)
}

function summaryFromInvoices(invoices: InvoiceEntity[]): DealEntity['invoiceSummary'] {
  const statuses = Array.from(new Set(invoices.map((entry) => entry.status)))
  const outstandingTotalCents = invoices.reduce(
    (sum, entry) => sum + Math.max(0, entry.outstandingCents ?? 0),
    0,
  )
  const countOf = (status: InvoiceEntity['status']): number =>
    invoices.filter((entry) => entry.status === status).length
  return {
    totalInvoices: invoices.length,
    paidInvoices: countOf('Paid'),
    unpaidInvoices: countOf('Unpaid'),
    partiallyPaidInvoices: countOf('Partially Paid'),
    scheduledInvoices: countOf('Scheduled'),
    draftInvoices: countOf('Draft'),
    voidedInvoices: countOf('Voided'),
    outstandingTotalCents,
    invoiceStatuses: statuses,
  }
}

function progressFromShipments(shipments: ShipmentEntity[]): DealEntity['shipmentProgress'] {
  const isDelivered = (shipment: ShipmentEntity): boolean => {
    const lastCustomerLeg = [...shipment.legs]
      .reverse()
      .find((leg) => leg.type === 'customer')
    return lastCustomerLeg?.status === 'delivered'
  }
  const totalDevices = shipments.reduce((sum, shipment) => sum + shipment.devices.length, 0)
  const deliveredDevices = shipments
    .filter(isDelivered)
    .reduce((sum, shipment) => sum + shipment.devices.length, 0)
  return {
    totalShip: shipments.length,
    deliveredShip: shipments.filter(isDelivered).length,
    totalDevices,
    deliveredDevices,
    hasShipments: shipments.length > 0,
  }
}

// Re-derives invoiceSummary on the parent deal after any invoice mutation.
// Mirrors the backend's server-side aggregate computation so the mock list
// page sees up-to-date drafts chip / payment filter state without refetch.
function recomputeInvoiceSummary(dealId: string): void {
  const deals = readDeals()
  const index = deals.findIndex((entry) => entry.id === dealId)
  if (index < 0) return
  const invoices = getInvoicesForDeal(dealId)
  const next = [...deals]
  next[index] = {
    ...deals[index],
    invoiceSummary: summaryFromInvoices(invoices),
    nextInvoiceSeq: invoices.length + 1,
    updatedTime: new Date().toISOString(),
  }
  writeDeals(next)
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function recomputeShipmentProgress(dealId: string): void {
  const deals = readDeals()
  const index = deals.findIndex((entry) => entry.id === dealId)
  if (index < 0) return
  const shipments = getShipmentsForDeal(dealId)
  const next = [...deals]
  next[index] = {
    ...deals[index],
    shipmentProgress: progressFromShipments(shipments),
    updatedTime: new Date().toISOString(),
  }
  writeDeals(next)
}

const mustFindInvoice = (dealId: string, invoiceId: string): InvoiceEntity => {
  const invoices = getInvoicesForDeal(dealId)
  const invoice = invoices.find((entry) => entry.id === invoiceId)
  if (!invoice) throw new Error(`mock dealsApi: invoice ${invoiceId} not found on deal ${dealId}`)
  return invoice
}

const notYetImplemented = <T>(name: string): Promise<T> =>
  Promise.reject(
    new Error(
      `mock dealsApi.${name} is a foundation stub; implementation lands in a follow-up change`,
    ),
  )

// ─── Deals ─────────────────────────────────────────────────────────────

export const listDeals = (query: ListDealsQuery): Promise<ListPaginatedDealsResult> => {
  const enriched = applyArchivedFlags(readDeals().map(cloneDeal))

  const statusFilter = isStatusFilter(query.status) ? query.status : undefined
  const paymentFilter = isPaymentFilter(query.payment) ? query.payment : undefined
  const search = (query.search ?? '').trim()

  const filtered = enriched.filter((deal) => {
    if (statusFilter && !dealMatchesStatus(deal, statusFilter)) return false
    if (paymentFilter && !dealMatchesPayment(deal, paymentFilter)) return false
    if (search && !dealMatchesSearch(deal, search)) return false
    return true
  })

  const page = query.page && query.page > 0 ? query.page : 1
  const pagePerRecordCount =
    query.pagePerRecordCount && query.pagePerRecordCount > 0
      ? Math.min(query.pagePerRecordCount, 500)
      : DEFAULT_PAGE_SIZE
  const start = (page - 1) * pagePerRecordCount
  const slice = filtered.slice(start, start + pagePerRecordCount)
  return delay({
    deals: slice,
    total: filtered.length,
    currentPage: page,
    pagePerRecordCount,
  })
}

export const getDeal = (dealId: string): Promise<DealEntity> => {
  const enriched = applyArchivedFlags([cloneDeal(mustFindDeal(dealId))])
  return delay(enriched[0])
}

// Mirrors prototype generateOrderNumber: MMDDYY + rep initials + monotonic
// counter scoped to the rep for the current month. Rep initials default to
// "JD" until the user-with-initials backend change ships.
function generateOrderNumber(deals: DealEntity[]): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const year = String(now.getFullYear()).slice(-2)
  return `D#${month}${day}${year}-JD-${deals.length + 1}`
}

function generateQuoteNumber(deals: DealEntity[]): string {
  const year = new Date().getFullYear()
  const sequence = String(deals.length + 1).padStart(4, '0')
  return `Q-${year}-${sequence}`
}

function nextDealId(deals: DealEntity[]): string {
  // Mock id is a numeric counter rendered as a string; real backend uses
  // MongoDB ObjectIds. Both shapes satisfy `Deal.id: string`.
  const max = deals.reduce((acc, deal) => {
    const numeric = Number(deal.id)
    return Number.isFinite(numeric) ? Math.max(acc, numeric) : acc
  }, 0)
  return String(max + 1)
}

export const createDeal = (body: CreateDealReqBody): Promise<DealEntity> => {
  const deals = readDeals()
  const id = nextDealId(deals)
  const orderNumber = generateOrderNumber(deals)
  const quoteNumber = generateQuoteNumber(deals)
  const now = new Date().toISOString()

  const newDeal: DealEntity = {
    id,
    orderNumber,
    quoteNumber,
    companyId: body.companyId,
    businessId: body.businessId,
    salesRepUserId: body.salesRepUserId,
    salesRepInitials: 'JD',
    customerName: body.customerName,
    customerEmail: body.customerEmail,
    shippingAddress: body.shippingAddress,
    billingCadence: body.billingCadence,
    pricingRegion: body.pricingRegion,
    pricingTerm: body.pricingTerm,
    activationDate: body.activationDate ?? null,
    expirationDate: body.expirationDate ?? null,
    lineItems: body.lineItems.map((item) => ({
      type: item.type,
      label: item.label,
      productKey: item.productKey,
      productName: item.productName,
      qty: item.qty,
      assignments: item.assignments,
    })),
    ccExecOn: body.ccExecOn,
    ccExecs: body.ccExecs,
    lifecycle: { status: 'active', canceledMode: null, canceledAt: null },
    archivedAt: null,
    nextInvoiceSeq: 1,
    invoiceSummary: {
      totalInvoices: 0,
      paidInvoices: 0,
      unpaidInvoices: 0,
      partiallyPaidInvoices: 0,
      scheduledInvoices: 0,
      draftInvoices: 0,
      voidedInvoices: 0,
      outstandingTotalCents: 0,
      invoiceStatuses: [],
    },
    shipmentProgress: {
      totalShip: 0,
      deliveredShip: 0,
      totalDevices: 0,
      deliveredDevices: 0,
      hasShipments: false,
    },
    createdTime: now,
    updatedTime: now,
    memo: body.memo,
  }

  writeDeals([...deals, newDeal])

  // Mirror backend createDeal side effect: auto-create a Draft first invoice
  // so the FirstInvoiceModal flow has something to render in mock mode.
  const firstInvoiceNumber = nextInvoiceNumber({
    order: orderNumber,
    nextInvoiceSeq: 1,
    invoiceCount: 0,
  })
  const firstInvoice: InvoiceEntity = {
    id: `mock-invoice-${id}-1`,
    dealId: id,
    createdTime: now,
    updatedTime: now,
    invoiceNumber: firstInvoiceNumber,
    period: {
      startDate: body.activationDate ?? null,
      endDate: null,
      label: '',
    },
    status: 'Draft',
    origin: 'auto',
    addonType: 'prorated',
    isCombinedRenewal: false,
    linkedShipmentIds: [],
    items: [],
    subtotalCents: 0,
    totalCents: 0,
    paidAmountCents: 0,
    outstandingCents: 0,
    paidDate: null,
    sentAt: null,
    year: 1,
  }
  setInvoicesForDeal(id, [firstInvoice])
  setShipmentsForDeal(id, [])
  recomputeInvoiceSummary(id)

  const persistedDeal = readDeals().find((entry) => entry.id === id) ?? newDeal
  return delay(cloneDeal(persistedDeal))
}

export const updateDeal = (
  dealId: string,
  body: UpdateDealReqBody,
): Promise<DealEntity> => {
  const deals = readDeals()
  const index = deals.findIndex((entry) => entry.id === dealId)
  if (index < 0) {
    return Promise.reject(new Error(`mock dealsApi.updateDeal: deal id=${dealId} not found`))
  }

  const current = deals[index]
  const merged: DealEntity = {
    ...current,
    companyId: body.companyId ?? current.companyId,
    businessId: body.businessId ?? current.businessId,
    salesRepUserId: body.salesRepUserId ?? current.salesRepUserId,
    billingCadence: body.billingCadence ?? current.billingCadence,
    pricingRegion: body.pricingRegion ?? current.pricingRegion,
    pricingTerm: body.pricingTerm ?? current.pricingTerm,
    activationDate: body.activationDate ?? current.activationDate,
    expirationDate: body.expirationDate ?? current.expirationDate,
    customerName: body.customerName ?? current.customerName,
    customerEmail: body.customerEmail ?? current.customerEmail,
    shippingAddress: body.shippingAddress ?? current.shippingAddress,
    lineItems: body.lineItems
      ? body.lineItems.map((item) => ({
          type: item.type,
          label: item.label,
          productKey: item.productKey,
          productName: item.productName,
          qty: item.qty,
          assignments: item.assignments,
        }))
      : current.lineItems,
    memo: body.memo ?? current.memo,
    ccExecOn: body.ccExecOn ?? current.ccExecOn,
    ccExecs: body.ccExecs ?? current.ccExecs,
    updatedTime: new Date().toISOString(),
  }

  const next = [...deals]
  next[index] = merged
  writeDeals(next)
  return delay(cloneDeal(merged))
}

export const archiveDeal = (dealId: string): Promise<DealEntity> => {
  mustFindDeal(dealId)
  const state = readState()
  const archivedSet = new Set(state.archivedDealIds)
  archivedSet.add(dealId)
  writeState({ ...state, archivedDealIds: Array.from(archivedSet) })
  return getDeal(dealId)
}

export const unarchiveDeal = (dealId: string): Promise<DealEntity> => {
  mustFindDeal(dealId)
  const state = readState()
  const archivedSet = new Set(state.archivedDealIds)
  archivedSet.delete(dealId)
  writeState({ ...state, archivedDealIds: Array.from(archivedSet) })
  return getDeal(dealId)
}

export const cancelDeal = (
  dealId: string,
  body: CancelDealReqBody,
): Promise<DealEntity> => {
  mustFindDeal(dealId)

  const state = readState()
  const nextState = {
    ...state,
    canceledDeals: {
      ...state.canceledDeals,
      [String(dealId)]: { mode: body.mode, canceledAt: new Date().toISOString() },
    },
  }
  writeState(nextState)

  // mode='immediate' voids every Scheduled invoice on the deal — mirrors the
  // backend behaviour so the UI sees the same end state.
  if (body.mode === 'immediate') {
    const invoices = getInvoicesForDeal(dealId)
    const next = invoices.map((invoice) =>
      invoice.status === 'Scheduled' ? { ...invoice, status: 'Voided' as const } : invoice,
    )
    setInvoicesForDeal(dealId, next)
    recomputeInvoiceSummary(dealId)
  }

  return getDeal(dealId)
}

export const uncancelDeal = (dealId: string): Promise<DealEntity> => {
  mustFindDeal(dealId)
  const state = readState()
  const nextCanceled = { ...state.canceledDeals }
  delete nextCanceled[String(dealId)]
  writeState({ ...state, canceledDeals: nextCanceled })
  return getDeal(dealId)
}

// ─── Invoices ──────────────────────────────────────────────────────────

export const listInvoices = (dealId: string): Promise<InvoiceEntity[]> => {
  mustFindDeal(dealId)
  return delay(getInvoicesForDeal(dealId).map((invoice) => ({ ...invoice })))
}

export const getInvoice = (dealId: string, invoiceId: string): Promise<InvoiceEntity> =>
  delay({ ...mustFindInvoice(dealId, invoiceId) })

function persistInvoice(
  dealId: string,
  invoiceId: string,
  mutate: (deal: DealEntity, invoice: InvoiceEntity, index: number) => InvoiceEntity,
): InvoiceEntity {
  const deal = mustFindDeal(dealId)
  const invoices = getInvoicesForDeal(dealId)
  const invoiceIndex = invoices.findIndex((entry) => entry.id === invoiceId)
  if (invoiceIndex < 0) {
    throw new Error(`mock dealsApi: invoice ${invoiceId} not found on deal ${dealId}`)
  }
  const nextInvoice = mutate(deal, invoices[invoiceIndex], invoiceIndex)
  const next = [...invoices]
  next[invoiceIndex] = nextInvoice
  setInvoicesForDeal(dealId, next)
  recomputeInvoiceSummary(dealId)
  return nextInvoice
}

export const createManualInvoice = (
  dealId: string,
  body: CreateManualInvoiceReqBody,
): Promise<InvoiceEntity> => {
  const deal = mustFindDeal(dealId)
  const invoices = getInvoicesForDeal(dealId)

  const invoiceNumber = nextInvoiceNumber({
    order: deal.orderNumber,
    nextInvoiceSeq: deal.nextInvoiceSeq,
    invoiceCount: invoices.length,
  })

  const startDate = body.periodStartDate ?? ''
  const endDate = body.periodEndDate ?? ''
  const period = {
    startDate,
    endDate,
    label: body.periodLabel ?? formatPeriodLabel(startDate, endDate),
  }

  const nowIso = new Date().toISOString()

  // Mock honors the body.status flag: callers passing `'Draft'` (AddInvoiceForm
  // Save as draft / Save & Send) get a Draft mock invoice; legacy callers
  // without a status field fall back to `Unpaid` to preserve prior behavior.
  const draft: InvoiceEntity = {
    id: `mock-invoice-${dealId}-${invoices.length + 1}`,
    dealId,
    createdTime: nowIso,
    updatedTime: nowIso,
    invoiceNumber,
    period,
    status: body.status ?? 'Unpaid',
    origin: 'manual',
    addonType: body.addonType,
    isCombinedRenewal: false,
    linkedShipmentIds: [],
    items: body.items.map((item) => ({
      ...item,
      rateCents: item.rateCents ?? 0,
      discountPercent: item.discountPercent ?? 0,
      amountCents: computeLineTotalCents(item),
    })),
    quoteNumber: body.quoteNumber,
    poNumber: body.poNumber,
    note: body.note,
    subtotalCents: 0,
    totalCents: 0,
    paidAmountCents: 0,
    outstandingCents: 0,
    paidDate: null,
    sentAt: null,
  }

  const prev = invoices[invoices.length - 1] ?? null
  const recalculated = recalculateInvoiceState(draft, prev, { inferStatus: false })

  setInvoicesForDeal(dealId, [...invoices, recalculated])
  recomputeInvoiceSummary(dealId)
  return delay({ ...recalculated })
}

export const generateNextInvoice = (dealId: string): Promise<InvoiceEntity> => {
  const deal = mustFindDeal(dealId)
  const invoices = getInvoicesForDeal(dealId)
  const lastAuto = [...invoices].reverse().find((entry) => entry.origin !== 'manual')
  const baseItems = lastAuto?.items ? lastAuto.items.map((item) => ({ ...item })) : []
  const invoiceNumber = nextInvoiceNumber({
    order: deal.orderNumber,
    nextInvoiceSeq: deal.nextInvoiceSeq,
    invoiceCount: invoices.length,
  })
  const seedYear =
    typeof lastAuto?.year === 'number' ? lastAuto.year + 1 : invoices.length + 1
  const period = lastAuto?.period
    ? {
        startDate: lastAuto.period.startDate,
        endDate: lastAuto.period.endDate,
        label: `Auto-renewal — period TBC (cloned from ${lastAuto.invoiceNumber})`,
      }
    : { startDate: '', endDate: '', label: 'Auto-renewal — period TBC' }
  const nowIso = new Date().toISOString()
  const draft: InvoiceEntity = {
    id: `mock-invoice-${dealId}-${invoices.length + 1}`,
    dealId,
    createdTime: nowIso,
    updatedTime: nowIso,
    invoiceNumber,
    period,
    status: 'Scheduled',
    origin: 'auto',
    isCombinedRenewal: false,
    linkedShipmentIds: [],
    items: baseItems.map((item) => ({ ...item, amountCents: computeLineTotalCents(item) })),
    subtotalCents: 0,
    totalCents: 0,
    paidAmountCents: 0,
    outstandingCents: 0,
    paidDate: null,
    sentAt: null,
    year: seedYear,
  }
  const prev = invoices[invoices.length - 1] ?? null
  const recalculated = recalculateInvoiceState(draft, prev, { inferStatus: false })
  setInvoicesForDeal(dealId, [...invoices, recalculated])
  recomputeInvoiceSummary(dealId)
  return delay({ ...recalculated })
}

export const updateInvoice = (
  dealId: string,
  invoiceId: string,
  body: UpdateInvoiceReqBody,
): Promise<InvoiceEntity> => {
  try {
    const next = persistInvoice(dealId, invoiceId, (_deal, invoice, index) => {
      const merged: InvoiceEntity = {
        ...invoice,
        ...(body.status !== undefined ? { status: body.status } : {}),
        ...(body.period !== undefined ? { period: body.period } : {}),
        ...(body.items !== undefined
          ? {
              items: body.items.map((item) => ({
                ...item,
                rateCents: item.rateCents ?? 0,
                discountPercent: item.discountPercent ?? 0,
                amountCents: computeLineTotalCents(item),
              })),
            }
          : {}),
        ...(body.paidAmountCents !== undefined
          ? { paidAmountCents: body.paidAmountCents }
          : {}),
        ...(body.paidDate !== undefined ? { paidDate: body.paidDate } : {}),
        ...(body.paidInfo !== undefined ? { paidInfo: body.paidInfo } : {}),
        ...(body.note !== undefined ? { note: body.note } : {}),
        ...(body.quoteNumber !== undefined ? { quoteNumber: body.quoteNumber } : {}),
        ...(body.poNumber !== undefined ? { poNumber: body.poNumber } : {}),
        ...(body.feeRecoveryCents !== undefined
          ? { feeRecoveryCents: body.feeRecoveryCents }
          : {}),
        ...(body.feeRecoveryNote !== undefined ? { feeRecoveryNote: body.feeRecoveryNote } : {}),
        ...(body.discountCents !== undefined ? { discountCents: body.discountCents } : {}),
        ...(body.discountNote !== undefined ? { discountNote: body.discountNote } : {}),
        ...(body.prevBalanceOverrideCents !== undefined
          ? { prevBalanceOverrideCents: body.prevBalanceOverrideCents }
          : {}),
      }
      const invoicesForDeal = getInvoicesForDeal(dealId)
      const prev = index > 0 ? invoicesForDeal[index - 1] : null
      return recalculateInvoiceState(merged, prev, { inferStatus: false })
    })
    return delay({ ...next })
  } catch (error) {
    return Promise.reject(error)
  }
}

export const voidInvoice = (
  dealId: string,
  invoiceId: string,
  _body?: VoidInvoiceReqBody,
): Promise<InvoiceEntity> => {
  try {
    const next = persistInvoice(dealId, invoiceId, (_deal, invoice) => ({
      ...invoice,
      status: 'Voided',
    }))
    return delay({ ...next })
  } catch (error) {
    return Promise.reject(error)
  }
}

export const restoreInvoice = (
  dealId: string,
  invoiceId: string,
): Promise<InvoiceEntity> => {
  try {
    const next = persistInvoice(dealId, invoiceId, (_deal, invoice, index) => {
      const paid = invoicePaidCents(invoice)
      const total = invoiceTotalCents(invoice)
      let restored: InvoiceEntity['status']
      if (paid === 0) restored = 'Unpaid'
      else if (paid >= total) restored = 'Paid'
      else restored = 'Partially Paid'
      const invoicesForDeal = getInvoicesForDeal(dealId)
      const prev = index > 0 ? invoicesForDeal[index - 1] : null
      return recalculateInvoiceState({ ...invoice, status: restored }, prev, { inferStatus: false })
    })
    return delay({ ...next })
  } catch (error) {
    return Promise.reject(error)
  }
}

// In-memory log of sent invoices, keyed by Idempotency-Key. The mock backend
// uses it to short-circuit duplicate sends so the dev console can confirm the
// real-API contract works end to end.
const sentInvoiceLog = new Map<
  string,
  { dealId: string; invoiceId: string; body: SendInvoiceReqBody; sentAt: string }
>()

export const sendInvoice = (
  dealId: string,
  invoiceId: string,
  body: SendInvoiceReqBody,
  idempotencyKey: string,
): Promise<InvoiceEntity> => {
  try {
    const existing = sentInvoiceLog.get(idempotencyKey)
    if (existing) {
      // eslint-disable-next-line no-console
      console.log('[mock dealsApi.sendInvoice] dedup hit', { idempotencyKey })
      return delay({ ...mustFindInvoice(dealId, invoiceId) })
    }
    const sentAt = new Date().toISOString()
    sentInvoiceLog.set(idempotencyKey, { dealId, invoiceId, body, sentAt })
    // eslint-disable-next-line no-console
    console.log('[mock dealsApi.sendInvoice]', {
      dealId,
      invoiceId,
      to: body.to,
      cc: body.cc,
      idempotencyKey,
      sentAt,
    })
    const next = persistInvoice(dealId, invoiceId, (_deal, invoice, index) => {
      const wasDraft = invoice.status === 'Draft'
      const baseStatus: InvoiceEntity['status'] = wasDraft ? 'Unpaid' : invoice.status
      const merged: InvoiceEntity = {
        ...invoice,
        status: baseStatus,
        sentAt,
        sentMeta: { to: body.to.trim(), at: sentAt },
        draftEmail: undefined,
      }
      const invoicesForDeal = getInvoicesForDeal(dealId)
      const prev = index > 0 ? invoicesForDeal[index - 1] : null
      return recalculateInvoiceState(merged, prev, { inferStatus: false })
    })
    return delay({ ...next })
  } catch (error) {
    return Promise.reject(error)
  }
}

export const getInvoicePdfBlob = (
  _dealId: string,
  _invoiceId: string,
): Promise<Blob> =>
  delay(new Blob(['mock pdf placeholder'], { type: 'application/pdf' }))

// ─── Shipments ─────────────────────────────────────────────────────────

export const listShipments = (dealId: string): Promise<ShipmentEntity[]> => {
  mustFindDeal(dealId)
  return delay(getShipmentsForDeal(dealId).map((shipment) => ({ ...shipment })))
}

export const createShipment = (
  _dealId: string,
  _body: CreateShipmentReqBody,
): Promise<ShipmentEntity> => notYetImplemented('createShipment')

export const updateShipment = (
  _dealId: string,
  _shipmentId: string,
  _body: UpdateShipmentReqBody,
): Promise<ShipmentEntity> => notYetImplemented('updateShipment')

export const deleteShipment = (
  _dealId: string,
  _shipmentId: string,
): Promise<void> => notYetImplemented('deleteShipment')

// ─── Catalog ───────────────────────────────────────────────────────────

export const listProducts = (): Promise<ProductGroupEntity[]> =>
  delay(PRODUCT_OPTIONS.map((group) => ({ ...group, items: group.items.map((item) => ({ ...item })) })))

// Internal helper kept for follow-up changes that need to mutate localStorage
// without re-implementing read/write plumbing. Not part of the public surface.
export const __internal = { readDeals, writeDeals }
